/**
 * Feb 27, 2026 · Pending Returnable Challans.
 *
 * Every Material Issue where Actual > Required auto-emits a Returnable
 * Material Challan pointing at the excess qty sitting on a department
 * floor. This screen is the accountability register: warehouse ticks
 * off "returned" or "consumed" so no floor material drifts silently.
 */
import { useEffect, useState, useMemo } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { ArrowsClockwiseIcon, WarningIcon, CheckCircleIcon, StackIcon, ArrowsLeftRightIcon } from "@phosphor-icons/react";

const fmt = n => Number(n || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });

const STATUS_CLS = {
  "PENDING": "border-amber-500/50 text-amber-300 bg-amber-500/10",
  "PARTIALLY RETURNED": "border-sky-500/50 text-sky-300 bg-sky-500/10",
  "CLOSED": "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
  "CANCELLED": "border-zinc-500/50 text-zinc-400 bg-zinc-500/10 [border-style:dashed]",
  // Legacy terminal states still show up in ALL / audit views.
  "RETURNED": "border-emerald-500/50 text-emerald-300 bg-emerald-500/10",
  "CONSUMED": "border-purple-500/50 text-purple-300 bg-purple-500/10",
};

// Feb 27, 2026 · Status filter options. `ACTIVE` = anything still with
// unreturned floor balance (PENDING + PARTIALLY RETURNED). This is the
// default so "Pending Returnable Challans" surfaces partials.
const STATUS_OPTIONS = ["ACTIVE", "PENDING", "PARTIALLY RETURNED", "CLOSED", "CANCELLED", "ALL"];

export default function PendingReturnableChallans() {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [status, setStatus] = useState("ACTIVE");
  const [godowns, setGodowns] = useState([]);
  const [xfer, setXfer] = useState(null);   // {row, from_id, to_id, qty, notes}

  const load = async () => {
    setLoading(true);
    try {
      const [{ data }, gd] = await Promise.all([
        api.get("/returnable-challans/", { params: status === "ALL" ? {} : { status } }),
        godowns.length ? Promise.resolve({ data: godowns }) : api.get("/godowns/"),
      ]);
      setRows(data || []);
      if (gd?.data) setGodowns(gd.data);
    } catch (e) { toast.error(e?.response?.data?.detail || "Failed to load"); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [status]);

  // Feb 27, 2026 · Remaining Balance on floor = excess − returned − consumed − (all transferred legs).
  // This is the qty warehouse still needs to reconcile before the RMC
  // can auto-close.
  const balanceOf = (r) => {
    const legs = (r.transfer_history || []).reduce((s, l) => s + Number(l.qty || 0), 0);
    return Math.max(0, Number(r.excess_qty || 0) - Number(r.returned_qty || 0) - Number(r.consumed_qty || 0) - legs);
  };

  const close = async (r) => {
    const rq = window.prompt(`Returned qty for ${r.challan_no} (max ${fmt(r.excess_qty)} ${r.uom}):`, "0");
    if (rq === null) return;
    const cq = window.prompt(`Consumed on floor for ${r.challan_no}:`, "0");
    if (cq === null) return;
    const notes = window.prompt("Notes (optional):", "") || "";
    try {
      const { data } = await api.post(`/returnable-challans/${r.id}/close`, {
        returned_qty: Number(rq) || 0,
        consumed_qty: Number(cq) || 0,
        notes,
      });
      toast.success(`${r.challan_no} · ${data.status}`);
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Close failed"); }
  };

  const total = rows.reduce((s, r) => s + Number(r.excess_qty || 0), 0);

  return (
    <div className="p-6 space-y-5" data-testid="returnable-challans-page">
      <div className="flex items-start justify-between gap-4">
        <div>
          <div className="text-[10px] uppercase tracking-widest text-zinc-500">Purchase &amp; Inventory</div>
          <h1 className="text-2xl font-semibold text-zinc-100 flex items-center gap-2 mt-1">
            <StackIcon size={26} weight="duotone" className="text-amber-400" />
            Pending Returnable Challans
          </h1>
          <p className="text-xs text-zinc-500 mt-1 max-w-2xl">
            Every Material Issue with Actual &gt; Required lands here. Warehouse ticks off returned / consumed qty so no
            excess floor material drifts silently.
          </p>
        </div>
        <div className="flex items-center gap-2">
          <select data-testid="rc-status-filter" value={status} onChange={e => setStatus(e.target.value)}
            className="h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] text-xs rounded-sm">
            {STATUS_OPTIONS.map(s => <option key={s} value={s}>{s}</option>)}
          </select>
          <button data-testid="rc-refresh" onClick={load} className="h-9 px-3 border border-[var(--border)] hover:bg-[var(--bg-2)] rounded-sm text-xs flex items-center gap-1.5">
            <ArrowsClockwiseIcon size={12} weight="bold" />Refresh
          </button>
        </div>
      </div>

      <div className="border border-[var(--border)] rounded-sm bg-[var(--bg-1)]">
        <div className="px-4 py-2 border-b border-[var(--border)] text-[11px] font-mono uppercase tracking-widest text-zinc-400 flex flex-wrap gap-4">
          <span>{rows.length} challan{rows.length===1?"":"s"}</span>
          <span>· Excess {fmt(total)}</span>
          <span>· <span className="text-amber-300">On-floor balance {fmt(rows.reduce((s,r)=>s+balanceOf(r),0))}</span></span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-xs" data-testid="rc-table">
            <thead className="bg-[var(--bg-2)] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="px-3 py-2 text-left">Challan No</th>
                <th className="px-3 py-2 text-left">Date</th>
                <th className="px-3 py-2 text-left">RM Code</th>
                <th className="px-3 py-2 text-right">Excess</th>
                <th className="px-3 py-2 text-right">Returned</th>
                <th className="px-3 py-2 text-right">Consumed</th>
                <th className="px-3 py-2 text-right">Transferred</th>
                <th className="px-3 py-2 text-right">Balance</th>
                <th className="px-3 py-2 text-left">Dept</th>
                <th className="px-3 py-2 text-left">CWO / MRQ</th>
                <th className="px-3 py-2 text-left">SO / ESO</th>
                <th className="px-3 py-2 text-left">Status</th>
                <th className="px-3 py-2 text-center w-24">—</th>
              </tr>
            </thead>
            <tbody>
              {loading && <tr><td colSpan={13} className="text-center py-8 text-zinc-500">Loading…</td></tr>}
              {!loading && rows.length === 0 && <tr><td colSpan={13} className="text-center py-8 text-zinc-500">
                <WarningIcon size={22} weight="fill" className="inline-block mr-2 text-amber-400" />
                No challans match — excess-issue floor is clean.
              </td></tr>}
              {rows.map(r => {
                const bal = balanceOf(r);
                const transferred = (r.transfer_history || []).reduce((s, l) => s + Number(l.qty || 0), 0);
                const active = r.status === "PENDING" || r.status === "PARTIALLY RETURNED";
                return (
                <tr key={r.id} data-testid={`rc-row-${r.id}`} className="border-t border-[var(--border)] hover:bg-[var(--bg-2)]/40">
                  <td className="px-3 py-2 font-mono text-[var(--accent)]">{r.challan_no}</td>
                  <td className="px-3 py-2 text-[10px] text-zinc-500">{(r.created_at||"").slice(0,10)}</td>
                  <td className="px-3 py-2 font-mono text-purple-300 truncate max-w-[200px]" title={r.rm_code}>{r.rm_code}</td>
                  <td className="px-3 py-2 text-right font-mono text-amber-300">{fmt(r.excess_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono text-emerald-300">{fmt(r.returned_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono text-sky-300">{fmt(r.consumed_qty)}</td>
                  <td className="px-3 py-2 text-right font-mono text-zinc-400">{fmt(transferred)}</td>
                  <td data-testid={`rc-balance-${r.id}`}
                      className={`px-3 py-2 text-right font-mono font-semibold ${bal > 0 ? "text-amber-200" : "text-emerald-400"}`}>
                    {fmt(bal)}
                  </td>
                  <td className="px-3 py-2 text-zinc-300">{r.department}</td>
                  <td className="px-3 py-2 text-[10px] font-mono text-zinc-400">{r.cwo_no || "—"}<div className="text-zinc-600">{r.mrq_no || "—"}</div></td>
                  <td className="px-3 py-2 text-[10px] font-mono text-zinc-400">{r.so_no || "—"}</td>
                  <td className="px-3 py-2">
                    <span className={`inline-flex items-center px-2 py-0.5 rounded-sm border text-[9px] font-mono uppercase tracking-widest ${STATUS_CLS[r.status] || ""}`}>{r.status}</span>
                  </td>
                  <td className="px-3 py-2 text-center">
                    {active && (
                      <div className="flex items-center justify-center gap-1">
                        <button data-testid={`rc-transfer-${r.id}`}
                          onClick={() => setXfer({ row: r, from_id: "", to_id: "", qty: "", notes: "" })}
                          title="Transfer to another department / RM-STORE"
                          className="h-7 px-2 border border-sky-500/40 text-sky-300 hover:bg-sky-500/10 rounded-sm text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                          <ArrowsLeftRightIcon size={11} weight="bold" />Transfer
                        </button>
                        <button data-testid={`rc-close-${r.id}`} onClick={() => close(r)} title="Record return / consumption"
                          className="h-7 px-2 border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/10 rounded-sm text-[10px] font-mono uppercase tracking-widest inline-flex items-center gap-1">
                          <CheckCircleIcon size={11} weight="bold" />Close
                        </button>
                      </div>
                    )}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      </div>

      {/* Feb 27, 2026 · RMC-Linked Transfer modal. Auto-fills RM/Batch
          from the picked challan and caps qty at the transferable balance. */}
      {xfer && (
        <div className="fixed inset-0 bg-black/60 z-50 flex items-center justify-center p-4" onClick={() => setXfer(null)}>
          <div className="bg-[var(--bg-1)] border border-[var(--border)] rounded-sm max-w-lg w-full p-5" onClick={e => e.stopPropagation()} data-testid="rc-xfer-modal">
            <div className="text-[11px] font-mono uppercase tracking-widest text-sky-300 mb-1">RMC-Linked Internal Transfer</div>
            <div className="text-sm text-zinc-200 font-mono">{xfer.row.challan_no} · {xfer.row.rm_code}</div>
            <div className="text-[10px] text-zinc-500 mt-1">
              SO: <span className="text-zinc-300">{xfer.row.so_no || "—"}</span> · CWO: <span className="text-zinc-300">{xfer.row.cwo_no || "—"}</span> ·
              Origin dept: <span className="text-zinc-300">{xfer.row.department}</span>
            </div>
            <div className="mt-3 grid grid-cols-2 gap-3">
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Source Department</label>
                <select data-testid="rc-xfer-from" value={xfer.from_id} onChange={e => setXfer(x => ({ ...x, from_id: e.target.value }))}
                  className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs">
                  <option value="">— current floor ({xfer.row.dest_godown_code || xfer.row.department}) —</option>
                  {godowns.map(g => <option key={g.id} value={g.id}>{g.code} · {g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Destination Department</label>
                <select data-testid="rc-xfer-to" value={xfer.to_id} onChange={e => setXfer(x => ({ ...x, to_id: e.target.value }))}
                  className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs">
                  <option value="">— select destination —</option>
                  {godowns.map(g => <option key={g.id} value={g.id}>{g.code} · {g.name}</option>)}
                </select>
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Qty · max {fmt(balanceOf(xfer.row))} {xfer.row.uom}</label>
                <input data-testid="rc-xfer-qty" type="number" step="0.001" value={xfer.qty} onChange={e => setXfer(x => ({ ...x, qty: e.target.value }))}
                  placeholder={`≤ ${fmt(balanceOf(xfer.row))}`}
                  className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono text-right" />
              </div>
              <div>
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Batch (optional)</label>
                <input value={xfer.row.batch_no || ""} disabled className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs font-mono text-zinc-500" />
              </div>
              <div className="col-span-2">
                <label className="text-[10px] uppercase tracking-widest text-zinc-500 block mb-1">Notes</label>
                <input data-testid="rc-xfer-notes" value={xfer.notes} onChange={e => setXfer(x => ({ ...x, notes: e.target.value }))}
                  placeholder="e.g. rerouted to WEAVING after WARPING excess" className="w-full h-9 px-2 bg-[var(--bg-0)] border border-[var(--border)] rounded-sm text-xs" />
              </div>
            </div>
            <div className="mt-4 flex justify-end gap-2">
              <button onClick={() => setXfer(null)} className="h-9 px-3 border border-[var(--border)] rounded-sm text-xs font-mono uppercase tracking-widest hover:bg-[var(--bg-2)]">Cancel</button>
              <button data-testid="rc-xfer-post" disabled={!xfer.to_id || !(Number(xfer.qty) > 0)}
                onClick={async () => {
                  try {
                    const { data } = await api.post(`/returnable-challans/${xfer.row.id}/transfer`, {
                      to_godown_id: xfer.to_id,
                      from_godown_id: xfer.from_id || undefined,
                      qty: Number(xfer.qty),
                      notes: xfer.notes.trim(),
                    });
                    toast.success(`${xfer.row.challan_no} · leg ${data.leg_no} posted · balance ${fmt(data.new_balance)}`);
                    setXfer(null); load();
                  } catch (e) { toast.error(e?.response?.data?.detail || "Transfer failed"); }
                }}
                className="h-9 px-4 bg-sky-500 hover:bg-sky-600 text-white rounded-sm text-xs font-mono uppercase tracking-widest disabled:opacity-40 disabled:cursor-not-allowed">
                Post Transfer
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
